<template>
  <li
    v-show="visible"
    :id="id"
    :class="containerKls"
    role="option"
    :aria-disabled="isDisabled || undefined"
    :aria-selected="itemSelected"
    @mouseenter="hoverItem"
    @click.stop="selectOptionClick"
  >
    <template v-if="isMultiple && componentName === currentComponentName">
      <jd-checkbox
        :disabled="disabled || isDisabled"
        :model-value="itemSelected"
        @click.stop="selectOptionClick"
      >
        <slot>
          <span>{{ currentLabel }}</span>
        </slot>
      </jd-checkbox>
    </template>
    <template v-else>
      <slot>
        <span>{{ currentLabel }}</span>
      </slot>
    </template>
  </li>
</template>

<script lang="ts">
// @ts-nocheck
import {
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  nextTick,
  onBeforeUnmount,
  reactive,
  toRefs,
  unref,
} from 'vue'
import JdCheckbox from '@jdesign-vue/components/checkbox'
import { useId, useNamespace } from '@jdesign-vue/hooks'
import { useOption } from './useOption'
import { selectKey } from './token'
import type { SelectOptionProxy } from './token'
const currentComponentName = 'JdOption'

export default defineComponent({
  name: currentComponentName,
  componentName: currentComponentName,
  components: {
    JdCheckbox,
  },

  props: {
    value: {
      required: true,
      type: [String, Number, Boolean, Object],
    },
    label: [String, Number],
    created: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const parentSelect = inject(selectKey)!
    const isMultiple = computed(() => parentSelect.props.multiple)
    const componentName = getCurrentInstance()?.type?.name

    const ns = useNamespace('select')
    const id = useId()
    const containerKls = computed(() => [
      ns.be('dropdown', 'item'),
      ns.is('disabled', unref(isDisabled)),
      {
        selected: unref(itemSelected),
        hover: unref(hover),
      },
    ])

    const states = reactive({
      index: -1,
      groupDisabled: false,
      visible: true,
      hitState: false,
      hover: false,
    })

    const { currentLabel, itemSelected, isDisabled, select, hoverItem } =
      useOption(props, states)

    const { visible, hover } = toRefs(states)

    const vm = getCurrentInstance().proxy

    select.onOptionCreate(vm as unknown as SelectOptionProxy)

    onBeforeUnmount(() => {
      const key = (vm as unknown as SelectOptionProxy).value
      const { selected } = select
      const selectedOptions = select.props.multiple ? selected : [selected]
      const doesSelected = selectedOptions.some((item) => {
        return item.value === (vm as unknown as SelectOptionProxy).value
      })
      // if option is not selected, remove it from cache
      nextTick(() => {
        if (select.cachedOptions.get(key) === vm && !doesSelected) {
          select.cachedOptions.delete(key)
        }
      })
      select.onOptionDestroy(key, vm)
    })

    function selectOptionClick(e?: PointerEvent) {
      if (e && e.target.tagName === 'INPUT') {
        return
      }
      if (props.disabled !== true && states.groupDisabled !== true) {
        select.handleOptionSelect(vm)
      }
    }

    return {
      ns,
      id,
      containerKls,
      currentLabel,
      itemSelected,
      isDisabled,
      select,
      hoverItem,
      visible,
      hover,
      selectOptionClick,
      states,
      isMultiple,
      componentName,
      currentComponentName,
    }
  },
})
</script>
